/**
 * @fileoverview Environment Variables Hook (client-safe)
 *
 * Provides centralized, typed access to client-safe environment variables.
 * Guards against exposing server-only secrets by only surfacing VITE-prefixed
 * or otherwise public values and normalizing booleans/strings.
 */

/**
 * Shape of client-safe environment variables.
 */

// Define ImportMetaEnv locally to avoid circular import
interface ImportMetaEnv extends ENV_TYPES {
  [key: string]: string | boolean | undefined;
}

interface ENV_TYPES {
  /* Logging Environment Variables */
  VITE_DEV?: boolean;
  VITE_ENABLE_LOGGING?: boolean;
  VITE_LOG_LEVEL?: string;
  VITE_LOGGING_SERVICE?: string;

  /* Tanstack Query Environment Variables */
  VITE_QUERY_STALE_TIME?: string;

  /* Supabase Environment Variables (public) */
  SUPABASE_PROJECT_ID?: string;
  SUPABASE_PUBLISHABLE_DEFAULT_KEY?: string;
  SUPABASE_PUBLISHABLE_KEY?: string;
  SUPABASE_URL?: string;
  WEBAUTHN_ALLOWED_ORIGINS?: string;

  /* Sentry Environment Variables (public) */
  SENTRY_DSN?: string;
  SENTRY_TRACES_SAMPLE_RATE?: string;
  SENTRY_TRACE_PROPAGATION_TARGETS?: string;
  SENTRY_ENABLE_LOGS?: boolean;
  SENTRY_SEND_PII?: boolean;

  /* Application Environment Variables */
  WEBSITE_URL?: string;
  WEBLOGO?: string;
  APP_NAME?: string;
  APP_SLUG?: string;
  APP_WEBSITE_URL?: string;
  APP_DESCRIPTION?: string;
  APP_KEY?: string;
  APP_VERSION?: string;
  APP_AUTHOR?: string;
  APP_AUTHOR_URL?: string;
  APP_COPYRIGHT?: string;
  APP_THEME_COLOR?: string;
  APP_FAVICON?: string;
  APP_LOGO?: string;
  APP_LOGO_ALT?: string;

  /* HCAPTCHA Environment Variables (public) */
  HCAPTCHA_SITE_KEY?: string;
}

interface AppVars {
  APP_NAME?: string;
  APP_SLUG?: string;
  APP_WEBSITE_URL?: string;
  APP_DESCRIPTION?: string;
  APP_VERSION?: string;
  APP_LOGO?: string;
  APP_LOGO_ALT?: string;
}

interface EnvironmentConfig {
  env: () => ImportMetaEnv;
  getEnv: <K extends keyof ImportMetaEnv>(
    key: K,
    defaultValue?: ImportMetaEnv[K],
  ) => ImportMetaEnv[K] | undefined;
  getString: <K extends keyof ImportMetaEnv>(
    key: K,
    defaultValue?: string,
  ) => string | undefined;
  getBoolean: <K extends keyof ImportMetaEnv>(
    key: K,
    defaultValue?: boolean,
  ) => boolean | undefined;
  logo: {
    LOGO: string;
    LOGO_ALT: string;
  };
  supabaseVars: {
    SUPABASE_PROJECT_ID?: string;
    SUPABASE_PUBLISHABLE_DEFAULT_KEY?: string;
    SUPABASE_PUBLISHABLE_KEY?: string;
    SUPABASE_URL?: string;
    WEBAUTHN_ALLOWED_ORIGINS?: string;
  };
  appVars: AppVars;
  sentryVars: {
    SENTRY_DSN?: string;
    SENTRY_TRACES_SAMPLE_RATE?: string;
    SENTRY_TRACE_PROPAGATION_TARGETS?: string;
    SENTRY_ENABLE_LOGS?: boolean;
    SENTRY_SEND_PII?: boolean;
  };
  hcaptchaVars: {
    HCAPTCHA_SITE_KEY?: string;
  };
  loggingVars: {
    VITE_DEV?: boolean;
    VITE_ENABLE_LOGGING?: boolean;
    VITE_LOG_LEVEL?: string;
    VITE_LOGGING_SERVICE?: string;
  };
  tanstackQueryVars: {
    VITE_QUERY_STALE_TIME?: string;
  };
}

type RawEnv = Record<string, string | boolean | undefined>;

// interface ImportMetaEnv extends ENV_TYPES {
//   [key: string]: string | boolean | undefined;
// }

// declare global {
//   interface ImportMeta {
//     env: ImportMetaEnv;
//   }
// }

const toStringEnv = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return undefined;
};

const toBooleanEnv = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return undefined;
};

const getEnvSource = (): RawEnv => {
  if (typeof import.meta !== 'undefined') {
    const metaEnv = (import.meta as { env?: RawEnv }).env;
    if (metaEnv) return metaEnv;
  }
  if (typeof process !== 'undefined' && typeof process.env !== 'undefined') {
    return process.env as RawEnv;
  }
  return {};
};

const resolveLogoPath = (logoPath?: string): string => {
  if (!logoPath) return '/assets/xanec-logo.png';

  const trimmed = logoPath.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  const baseUrl = toStringEnv(
    typeof import.meta !== 'undefined'
      ? (import.meta as { env?: RawEnv }).env?.BASE_URL
      : undefined,
  );

  const normalizedBase = (baseUrl ?? '/').replace(/\/$/, '');
  const normalizedPath = trimmed.replace(/^public\//i, '').replace(/^\//, '');

  return `${normalizedBase}/${normalizedPath || 'assets/xanec-logo.png'}`;
};

const buildEnv = (raw: RawEnv): ImportMetaEnv => ({
  /* Logging Environment Variables */
  VITE_DEV: toBooleanEnv(raw.VITE_DEV),
  VITE_ENABLE_LOGGING: toBooleanEnv(raw.VITE_ENABLE_LOGGING),
  VITE_LOG_LEVEL: toStringEnv(raw.VITE_LOG_LEVEL),
  VITE_LOGGING_SERVICE: toStringEnv(raw.VITE_LOGGING_SERVICE),

  /* Supabase Environment Variables (public) */
  SUPABASE_PROJECT_ID: toStringEnv(
    raw.VITE_SUPABASE_PROJECT_ID ?? raw.SUPABASE_PROJECT_ID,
  ),
  SUPABASE_PUBLISHABLE_DEFAULT_KEY: toStringEnv(
    raw.VITE_SUPABASE_PUBLISHABLE_DEFAULT_KEY ??
      raw.SUPABASE_PUBLISHABLE_DEFAULT_KEY,
  ),
  SUPABASE_PUBLISHABLE_KEY: toStringEnv(
    raw.VITE_SUPABASE_PUBLISHABLE_KEY ?? raw.SUPABASE_PUBLISHABLE_KEY,
  ),
  SUPABASE_URL: toStringEnv(raw.VITE_SUPABASE_URL ?? raw.SUPABASE_URL),
  WEBAUTHN_ALLOWED_ORIGINS: toStringEnv(
    raw.VITE_WEBAUTHN_ALLOWED_ORIGINS ?? raw.WEBAUTHN_ALLOWED_ORIGINS,
  ),

  /* Sentry Environment Variables (public) */
  SENTRY_DSN: toStringEnv(raw.VITE_SENTRY_DSN ?? raw.SENTRY_DSN),
  SENTRY_TRACES_SAMPLE_RATE: toStringEnv(
    raw.VITE_SENTRY_TRACES_SAMPLE_RATE ?? raw.SENTRY_TRACES_SAMPLE_RATE,
  ),
  SENTRY_TRACE_PROPAGATION_TARGETS: toStringEnv(
    raw.VITE_SENTRY_TRACE_PROPAGATION_TARGETS ??
      raw.SENTRY_TRACE_PROPAGATION_TARGETS,
  ),
  SENTRY_ENABLE_LOGS: toBooleanEnv(
    raw.VITE_SENTRY_ENABLE_LOGS ?? raw.SENTRY_ENABLE_LOGS,
  ),
  SENTRY_SEND_PII: toBooleanEnv(
    raw.VITE_SENTRY_SEND_PII ?? raw.SENTRY_SEND_PII,
  ),

  /* Application Environment Variables */
  WEBSITE_URL: toStringEnv(raw.VITE_WEBSITE_URL ?? raw.WEBSITE_URL),
  WEBLOGO: toStringEnv(raw.VITE_WEBLOGO ?? raw.WEBLOGO),
  APP_NAME: toStringEnv(raw.VITE_APP_NAME ?? raw.APP_NAME),
  APP_SLUG: toStringEnv(raw.VITE_APP_SLUG ?? raw.APP_SLUG),
  APP_WEBSITE_URL: toStringEnv(raw.VITE_APP_WEBSITE_URL ?? raw.APP_WEBSITE_URL),
  APP_DESCRIPTION: toStringEnv(raw.VITE_APP_DESCRIPTION ?? raw.APP_DESCRIPTION),
  APP_KEY: toStringEnv(raw.VITE_APP_KEY ?? raw.APP_KEY),
  APP_VERSION: toStringEnv(raw.VITE_APP_VERSION ?? raw.APP_VERSION),
  APP_AUTHOR: toStringEnv(raw.VITE_APP_AUTHOR ?? raw.APP_AUTHOR),
  APP_AUTHOR_URL: toStringEnv(raw.VITE_APP_AUTHOR_URL ?? raw.APP_AUTHOR_URL),
  APP_COPYRIGHT: toStringEnv(raw.VITE_APP_COPYRIGHT ?? raw.APP_COPYRIGHT),
  APP_THEME_COLOR: toStringEnv(raw.VITE_APP_THEME_COLOR ?? raw.APP_THEME_COLOR),
  APP_FAVICON: toStringEnv(raw.VITE_APP_FAVICON ?? raw.APP_FAVICON),
  APP_LOGO: toStringEnv(raw.VITE_APP_LOGO ?? raw.APP_LOGO),
  APP_LOGO_ALT: toStringEnv(raw.VITE_APP_LOGO_ALT ?? raw.APP_LOGO_ALT),

  /* HCAPTCHA Environment Variables (public) */
  HCAPTCHA_SITE_KEY: toStringEnv(
    raw.VITE_HCAPTCHA_SITE_KEY ?? raw.HCAPTCHA_SITE_KEY,
  ),
});

const getEnvironment = (): EnvironmentConfig => {
  const _env: ImportMetaEnv = buildEnv(getEnvSource());

  const getEnv = <K extends keyof ImportMetaEnv>(
    key: K,
    defaultValue?: ImportMetaEnv[K],
  ): ImportMetaEnv[K] | undefined => {
    return _env[key] ?? defaultValue;
  };

  const getString = <K extends keyof ImportMetaEnv>(
    key: K,
    defaultValue?: string,
  ): string | undefined => {
    const value = _env[key];
    return typeof value === 'string' && value.length > 0 ? value : defaultValue;
  };

  const getBoolean = <K extends keyof ImportMetaEnv>(
    key: K,
    defaultValue?: boolean,
  ): boolean | undefined => {
    const value = _env[key];
    return typeof value === 'boolean' ? value : defaultValue;
  };

  const loggingVars = {
    VITE_DEV: getBoolean('VITE_DEV', false),
    VITE_ENABLE_LOGGING: getBoolean('VITE_ENABLE_LOGGING', true),
    VITE_LOG_LEVEL: getString('VITE_LOG_LEVEL', 'WARN'),
    VITE_LOGGING_SERVICE: getString('VITE_LOGGING_SERVICE', 'console'),
  };

  const supabaseVars = {
    SUPABASE_PROJECT_ID: getString('SUPABASE_PROJECT_ID'),
    SUPABASE_PUBLISHABLE_DEFAULT_KEY: getString(
      'SUPABASE_PUBLISHABLE_DEFAULT_KEY',
    ),
    SUPABASE_PUBLISHABLE_KEY: getString('SUPABASE_PUBLISHABLE_KEY'),
    SUPABASE_URL: getString('SUPABASE_URL'),
    WEBAUTHN_ALLOWED_ORIGINS: getString('WEBAUTHN_ALLOWED_ORIGINS'),
  };

  const sentryVars = {
    SENTRY_DSN: getString('SENTRY_DSN'),
    SENTRY_TRACES_SAMPLE_RATE: getString('SENTRY_TRACES_SAMPLE_RATE'),
    SENTRY_TRACE_PROPAGATION_TARGETS: getString(
      'SENTRY_TRACE_PROPAGATION_TARGETS',
    ),
    SENTRY_ENABLE_LOGS: getBoolean('SENTRY_ENABLE_LOGS'),
    SENTRY_SEND_PII: getBoolean('SENTRY_SEND_PII'),
  };

  const appVars: AppVars = {
    APP_NAME: getString('APP_NAME', 'Xanec Technologies'),
    APP_SLUG: getString('APP_SLUG', 'xtech'),
    APP_WEBSITE_URL: getString('APP_WEBSITE_URL', 'https://www.xanectech.com'),
    APP_DESCRIPTION: getString(
      'APP_DESCRIPTION',
      'Secure Data Solutions for the Modern Enterprise',
    ),
    APP_VERSION: getString('APP_VERSION', '1.0.0'),
    APP_LOGO: resolveLogoPath(getString('APP_LOGO', '/assets/xanec-logo.png')),
    APP_LOGO_ALT: getString('APP_LOGO_ALT', 'Xanec Technologies'),
  };

  const hcaptchaVars = {
    HCAPTCHA_SITE_KEY: getString('HCAPTCHA_SITE_KEY'),
  };

  const logo = {
    LOGO: appVars.APP_LOGO ?? '/assets/xanec-logo.png',
    LOGO_ALT: appVars.APP_LOGO_ALT ?? 'Xanec Technologies',
  };

  const tanstackQueryVars = {
    VITE_QUERY_STALE_TIME: getString('VITE_QUERY_STALE_TIME', '300000'),
  };

  const env = () => _env;

  return {
    env,
    getEnv,
    getString,
    getBoolean,
    logo,
    supabaseVars,
    appVars,
    sentryVars,
    hcaptchaVars,
    loggingVars,
    tanstackQueryVars,
  };
};

let cachedEnv: EnvironmentConfig | null = null;

const getEnvironmentConfig = (): EnvironmentConfig => {
  if (!cachedEnv) {
    cachedEnv = getEnvironment();
  }
  return cachedEnv;
};

const useEnvironment = (): EnvironmentConfig => {
  return getEnvironmentConfig();
};

export {
  useEnvironment,
  getEnvironmentConfig,
  type ENV_TYPES,
  type ImportMetaEnv,
  type EnvironmentConfig,
};
